<template>
  <div class="admin-container">
    <!-- 视图切换和头部操作 -->
    <div class="view-toggle">
      <button @click="isManagementView = true" :class="{active: isManagementView}">管理视图</button>
    </div>

    <div class="header-actions">
      <h1>{{ isManagementView ? '解决方案管理' : '解决方案中心' }}</h1>
      <button v-if="isManagementView" @click="showAddForm" class="btn-add">添加解决方案</button>
    </div>

    <!-- 管理视图内容 -->
    <SolutionFilter @search="handleSearch" />
    <div class="table-container">
      <SolutionTable 
        :solutions="solutions"
        :loading="loading"
        @edit="handleEdit"
        @paragraphs="showParagraphManagement"

        @delete="handleDeleteConfirm"
        :columns="['title', 'category', 'description', 'actions']"
      />
    </div>



    <!-- 段落管理界面 -->
    <div v-if="selectedSolutionForParagraphs" class="paragraph-management">
      <div class="paragraph-header">
        <h2>{{ selectedSolutionForParagraphs.title }} - 段落管理</h2>
        <div class="paragraph-actions">
          <button @click="showAddParagraphForm" class="btn-add">添加段落</button>
          <button @click="selectedSolutionForParagraphs = null" class="btn-close">关闭</button>
        </div>
      </div>
      
      <div class="paragraph-list">
        <div v-if="solutionParagraphs.length === 0" class="no-paragraphs">
          暂无段落内容
        </div>
        <div v-else class="paragraph-items">
          <div 
            v-for="(paragraph, index) in solutionParagraphs" 
            :key="paragraph.id" 
            class="paragraph-item"
            draggable="true"
            @dragstart="handleDragStart(paragraph, index)"
            @dragover.prevent
            @drop="handleDrop(index)"
          >
            <div class="paragraph-drag-handle">⋮⋮</div>
            <div class="paragraph-info">
              <h4>{{ paragraph.title }}</h4>
              <p class="paragraph-type">类型: {{ getContentTypeLabel(paragraph.content_type) }}</p>
              <p class="paragraph-preview">{{ getContentPreview(paragraph.content) }}</p>
              <span class="paragraph-meta">排序: {{ paragraph.order_index }} | 状态: {{ paragraph.is_active ? '启用' : '禁用' }}</span>
            </div>
            <div class="paragraph-actions">
              <button @click="editParagraph(paragraph)" class="btn-edit">编辑</button>
              <button @click="toggleParagraphStatus(paragraph)" :class="paragraph.is_active ? 'btn-disable' : 'btn-enable'">
                {{ paragraph.is_active ? '禁用' : '启用' }}
              </button>
              <button @click="handleDeleteParagraph(paragraph.id)" class="btn-delete">删除</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 段落表单 -->
    <div v-if="showParagraphForm" class="form-overlay">
      <div class="paragraph-form">
        <h3>{{ editingParagraph ? '编辑段落' : '添加段落' }}</h3>
        <form @submit.prevent="saveParagraph">
          <div class="form-group">
            <label>段落标题</label>
            <input 
              type="text" 
              v-model="paragraphForm.title" 
              :class="{ error: paragraphErrors.title }"
              required
            >
            <span v-if="paragraphErrors.title" class="error-text">{{ paragraphErrors.title }}</span>
          </div>
          
          <div class="form-group">
            <label>内容类型</label>
            <select v-model="paragraphForm.content_type" @change="handleContentTypeChange">
              <option value="text">文本内容</option>
              <option value="table">表格数据</option>
              <option value="image">图片</option>
              <option value="mixed">混合内容</option>
            </select>
          </div>
          
          <!-- 文本内容 -->
          <div v-if="paragraphForm.content_type === 'text'" class="form-group">
            <label>文本内容</label>
            <div class="rich-text-editor">
              <div class="editor-toolbar">
                <button type="button" @click="formatText('bold')" class="format-btn" title="加粗">B</button>
                <button type="button" @click="formatText('italic')" class="format-btn" title="斜体">I</button>
                <button type="button" @click="formatText('underline')" class="format-btn" title="下划线">U</button>
                <button type="button" @click="formatText('fontSize', '14px')" class="format-btn" title="小字号">小</button>
                <button type="button" @click="formatText('fontSize', '16px')" class="format-btn" title="中字号">中</button>
                <button type="button" @click="formatText('fontSize', '18px')" class="format-btn" title="大字号">大</button>
                <button type="button" @click="formatText('justifyLeft')" class="format-btn" title="左对齐">←</button>
                <button type="button" @click="formatText('justifyCenter')" class="format-btn" title="居中">⊙</button>
                <button type="button" @click="formatText('justifyRight')" class="format-btn" title="右对齐">→</button>
                <button type="button" @click="formatText('indent')" class="format-btn" title="增加缩进">→|</button>
                <button type="button" @click="formatText('outdent')" class="format-btn" title="减少缩进">|←</button>
                <button type="button" @click="clearFormatting" class="format-btn" title="清除格式">清除</button>
              </div>
              <div 
                ref="richTextEditor"
                class="rich-text-content"
                :class="{ error: paragraphErrors.content }"
                contenteditable="true"
                @input="handleRichTextInput"
                @paste="handleRichTextPaste"
                @blur="updateParagraphContent"
                style="min-height: 200px; padding: 12px; border: 1px solid #ddd; border-radius: 4px; outline: none;"
                placeholder="输入文本内容，支持从Word复制带格式的文本"
              ></div>
            </div>
            <span v-if="paragraphErrors.content" class="error-text">{{ paragraphErrors.content }}</span>
          </div>
          
          <!-- 表格内容 -->
          <div v-if="paragraphForm.content_type === 'table'" class="form-group">
            <label>表格数据</label>
            
            <!-- 表格输入方式选择 -->
            <div class="table-input-options">
              <label class="radio-option">
                <input type="radio" v-model="tableInputMode" value="visual" @change="handleTableInputModeChange">
                <span>可视化编辑</span>
              </label>
              <label class="radio-option">
                <input type="radio" v-model="tableInputMode" value="import" @change="handleTableInputModeChange">
                <span>文件导入</span>
              </label>
              <label class="radio-option">
                <input type="radio" v-model="tableInputMode" value="json" @change="handleTableInputModeChange">
                <span>JSON格式</span>
              </label>
            </div>
            
            <!-- 可视化表格编辑器 -->
            <div v-if="tableInputMode === 'visual'" class="visual-table-editor">
              <div class="table-controls">
                <button type="button" @click="addTableColumn" class="btn-table-action">+ 添加列</button>
                <button type="button" @click="addTableRow" class="btn-table-action">+ 添加行</button>
                <button type="button" @click="clearTable" class="btn-table-action btn-danger">清空表格</button>
              </div>
              
              <div class="table-editor-container">
                <table class="editable-table">
                  <thead>
                    <tr>
                      <th v-for="(header, index) in tableData.headers" :key="'header-' + index" class="editable-header">
                        <input 
                          type="text" 
                          v-model="tableData.headers[index]" 
                          placeholder="列标题"
                          class="header-input"
                        >
                        <button 
                          type="button" 
                          @click="removeTableColumn(index)" 
                          class="btn-remove-column"
                          :disabled="tableData.headers.length <= 1"
                        >×</button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rowIndex) in tableData.rows" :key="'row-' + rowIndex">
                      <td v-for="(cell, cellIndex) in row" :key="'cell-' + rowIndex + '-' + cellIndex" class="editable-cell">
                        <input 
                          type="text" 
                          v-model="tableData.rows[rowIndex][cellIndex]" 
                          placeholder="单元格内容"
                          class="cell-input"
                        >
                      </td>
                      <td class="row-actions">
                        <button 
                          type="button" 
                          @click="removeTableRow(rowIndex)" 
                          class="btn-remove-row"
                          :disabled="tableData.rows.length <= 1"
                        >删除行</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <!-- 文件导入 -->
            <div v-if="tableInputMode === 'import'" class="table-import-section">
              <div class="import-options">
                <label class="file-upload-label">
                  <input 
                    type="file" 
                    @change="handleTableFileSelect"
                    accept=".csv,.xlsx,.xls"
                    class="file-input"
                  >
                  <span class="file-upload-btn">📁 选择文件 (CSV, Excel)</span>
                </label>
                <div class="import-tips">
                  支持 CSV 和 Excel 文件格式，第一行将作为表头
                </div>
              </div>
              
              <!-- 导入预览 -->
              <div v-if="importPreviewData" class="import-preview">
                <h4>导入预览</h4>
                <div class="preview-table-container">
                  <table class="preview-table">
                    <thead>
                      <tr>
                        <th v-for="header in importPreviewData.headers" :key="header">{{ header }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in importPreviewData.rows.slice(0, 5)" :key="index">
                        <td v-for="cell in row" :key="cell">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-if="importPreviewData.rows.length > 5" class="preview-more">
                    ... 还有 {{ importPreviewData.rows.length - 5 }} 行数据
                  </div>
                </div>
                <div class="import-actions">
                  <button type="button" @click="confirmTableImport" class="btn-confirm">确认导入</button>
                  <button type="button" @click="cancelTableImport" class="btn-cancel">取消</button>
                </div>
              </div>
            </div>
            
            <!-- JSON格式输入 -->
            <div v-if="tableInputMode === 'json'" class="json-input-section">
              <textarea 
                v-model="tableJsonContent" 
                :class="{ error: paragraphErrors.content }"
                rows="10"
                placeholder='{
  "headers": ["列1", "列2", "列3"],
  "rows": [
    ["数据1", "数据2", "数据3"],
    ["数据4", "数据5", "数据6"]
  ]
}'
                @input="handleJsonInput"
                required
              ></textarea>
            </div>
            
            <span v-if="paragraphErrors.content" class="error-text">{{ paragraphErrors.content }}</span>
          </div>
          
          <!-- 图片内容 -->
          <div v-if="paragraphForm.content_type === 'image'" class="form-group">
            <label>图片上传</label>
            <div class="image-upload-container">
              <input 
                type="file" 
                ref="imageFileInput"
                @change="handleImageUpload"
                accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
                class="file-input"
                id="image-upload"
              >
              <label for="image-upload" class="file-upload-btn">
                <span v-if="!selectedImageFile">📁 选择图片文件</span>
                <span v-else>📁 {{ selectedImageFile.name }}</span>
              </label>
              <div class="upload-tips">
                支持 PNG、JPG、JPEG、GIF、WEBP 格式，文件大小不超过 10MB
              </div>
            </div>
            <span v-if="paragraphErrors.content" class="error-text">{{ paragraphErrors.content }}</span>
            
            <!-- 图片预览 -->
            <div v-if="imagePreviewUrl" class="image-preview">
              <img :src="imagePreviewUrl" alt="预览" style="max-width: 200px; max-height: 200px; border-radius: 4px; border: 1px solid #ddd;" />
              <button type="button" @click="removeImage" class="remove-image-btn">移除图片</button>
            </div>
            
            <!-- 或者手动输入URL -->
            <div class="manual-url-section">
              <label>
                <input type="checkbox" v-model="useManualUrl" @change="handleUrlModeChange"> 
                手动输入图片URL
              </label>
              <div v-if="useManualUrl" class="manual-url-input">
                <input 
                  type="url" 
                  v-model="paragraphForm.content" 
                  :class="{ error: paragraphErrors.content }"
                  placeholder="输入图片URL地址"
                  required
                >
              </div>
            </div>
          </div>
          
          <!-- 混合内容 -->
          <div v-if="paragraphForm.content_type === 'mixed'" class="form-group">
            <label>混合内容</label>
            
            <!-- 输入模式切换 -->
            <div class="input-mode-tabs">
              <button 
                type="button" 
                :class="{ active: mixedInputMode === 'visual' }"
                @click="mixedInputMode = 'visual'; handleMixedInputModeChange()"
              >
                可视化编辑
              </button>
              <button 
                type="button" 
                :class="{ active: mixedInputMode === 'json' }"
                @click="mixedInputMode = 'json'; handleMixedInputModeChange()"
              >
                JSON编辑
              </button>
            </div>
            
            <!-- 可视化编辑模式 -->
            <div v-if="mixedInputMode === 'visual'" class="mixed-content-editor">
              <!-- 内容块列表 -->
              <div class="content-blocks">
                <div v-for="(block, index) in mixedContentBlocks" :key="index" class="content-block">
                  <div class="block-header">
                    <span class="block-type">{{ block.type === 'text' ? '文本' : block.type === 'image' ? '图片' : '表格' }}</span>
                    <div class="block-actions">
                      <button type="button" @click="moveMixedContentBlock(index, 'up')" :disabled="index === 0">↑</button>
                      <button type="button" @click="moveMixedContentBlock(index, 'down')" :disabled="index === mixedContentBlocks.length - 1">↓</button>
                      <button type="button" @click="removeMixedContentBlock(index)" class="delete-btn">删除</button>
                    </div>
                  </div>
                  
                  <!-- 文本块编辑 -->
                  <div v-if="block.type === 'text'" class="text-block-editor">
                    <div class="rich-text-editor">
                      <div class="editor-toolbar">
                        <button type="button" class="format-btn" @click="formatMixedText('bold', index)" title="加粗">B</button>
                        <button type="button" class="format-btn" @click="formatMixedText('italic', index)" title="斜体">I</button>
                        <button type="button" class="format-btn" @click="formatMixedText('underline', index)" title="下划线">U</button>
                        <button type="button" class="format-btn" @click="formatMixedTextWithValue('fontSize', '14px', index)" title="小字号">小</button>
                        <button type="button" class="format-btn" @click="formatMixedTextWithValue('fontSize', '16px', index)" title="中字号">中</button>
                        <button type="button" class="format-btn" @click="formatMixedTextWithValue('fontSize', '18px', index)" title="大字号">大</button>
                        <button type="button" class="format-btn" @click="formatMixedText('justifyLeft', index)" title="左对齐">←</button>
                        <button type="button" class="format-btn" @click="formatMixedText('justifyCenter', index)" title="居中">⊙</button>
                        <button type="button" class="format-btn" @click="formatMixedText('justifyRight', index)" title="右对齐">→</button>
                        <button type="button" class="format-btn" @click="formatMixedText('indent', index)" title="增加缩进">→|</button>
                        <button type="button" class="format-btn" @click="formatMixedText('outdent', index)" title="减少缩进">|←</button>
                        <button type="button" class="format-btn" @click="clearMixedFormatting(index)" title="清除格式">清除</button>
                      </div>
                      <div 
                        :ref="el => mixedRichTextEditors[index] = el"
                        class="rich-text-content"
                        contenteditable="true"
                        :placeholder="'请输入文本内容'"
                        @input="handleMixedRichTextInput(index)"
                        @paste="handleMixedRichTextPaste($event, index)"
                        v-html="block.content"
                      ></div>
                    </div>
                  </div>
                  
                  <!-- 图片块编辑 -->
                  <div v-else-if="block.type === 'image'" class="image-block-editor">
                    <div class="image-upload-options">
                      <label>
                        <input type="radio" :value="false" v-model="block.useManualUrl" @change="resetMixedImageBlock(index)">
                        上传图片文件
                      </label>
                      <label>
                        <input type="radio" :value="true" v-model="block.useManualUrl" @change="resetMixedImageBlock(index)">
                        手动输入URL
                      </label>
                    </div>
                    
                    <!-- 文件上传模式 -->
                    <div v-if="!block.useManualUrl" class="file-upload-section">
                      <div class="form-group">
                        <label>选择图片文件:</label>
                        <input 
                          type="file" 
                          accept="image/*" 
                          @change="handleMixedImageFileSelect($event, index)"
                          class="file-input"
                        >
                      </div>
                      <div v-if="block.selectedFile" class="file-info">
                        <span>已选择: {{ block.selectedFile.name }}</span>
                      </div>
                    </div>
                    
                    <!-- 手动URL模式 -->
                    <div v-else class="manual-url-section">
                      <div class="form-group">
                        <label>图片URL:</label>
                        <input type="url" v-model="block.content" placeholder="请输入图片URL地址">
                      </div>
                    </div>
                    
                    <div class="form-group">
                      <label>图片描述:</label>
                      <input type="text" v-model="block.alt" placeholder="请输入图片描述">
                    </div>
                    
                    <!-- 图片预览 -->
                    <div v-if="block.previewUrl || block.content" class="image-preview">
                      <img :src="block.previewUrl || block.content" :alt="block.alt" style="max-width: 200px; max-height: 150px;">
                    </div>
                  </div>
                  
                  <!-- 表格块编辑 -->
                  <div v-else-if="block.type === 'table'" class="table-block-editor">
                    <div class="table-controls">
                      <button type="button" @click="addTableRowToMixedBlock(index)">添加行</button>
                      <button type="button" @click="addTableColumnToMixedBlock(index)">添加列</button>
                    </div>
                    <table class="editable-table">
                      <thead>
                        <tr>
                          <th v-for="(header, colIndex) in block.content.headers" :key="colIndex">
                            <input v-model="block.content.headers[colIndex]" placeholder="列标题">
                            <button type="button" @click="removeTableColumnFromMixedBlock(index, colIndex)" :disabled="block.content.headers.length <= 1" class="remove-col-btn">×</button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, rowIndex) in block.content.rows" :key="rowIndex">
                          <td v-for="(cell, colIndex) in row" :key="colIndex">
                            <input v-model="block.content.rows[rowIndex][colIndex]" placeholder="单元格内容">
                          </td>
                          <td class="row-actions">
                            <button type="button" @click="removeTableRowFromMixedBlock(index, rowIndex)" :disabled="block.content.rows.length <= 1" class="remove-row-btn">删除行</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              <!-- 添加内容块按钮 -->
              <div class="add-block-section">
                <div v-if="!showAddBlockMenu" class="add-block-trigger">
                  <button type="button" @click="showAddBlockMenu = true" class="add-block-btn">+ 添加内容块</button>
                </div>
                <div v-else class="add-block-menu">
                  <button type="button" @click="addMixedContentBlock('text')" class="block-type-btn">📝 文本</button>
                  <button type="button" @click="addMixedContentBlock('image')" class="block-type-btn">🖼️ 图片</button>
                  <button type="button" @click="addMixedContentBlock('table')" class="block-type-btn">📊 表格</button>
                  <button type="button" @click="showAddBlockMenu = false" class="cancel-btn">取消</button>
                </div>
              </div>
            </div>
            
            <!-- JSON编辑模式 -->
            <div v-else class="json-editor">
              <textarea 
                v-model="mixedJsonContent" 
                :class="{ error: paragraphErrors.content }"
                rows="12"
                placeholder='[
  {
    "type": "text",
    "content": "这是文本内容"
  },
  {
    "type": "image",
    "content": "图片URL地址",
    "alt": "图片描述"
  },
  {
    "type": "table",
    "content": {
      "headers": ["列1", "列2"],
      "rows": [["数据1", "数据2"]]
    }
  }
]'
              ></textarea>
            </div>
            
            <span v-if="paragraphErrors.content" class="error-text">{{ paragraphErrors.content }}</span>
          </div>
          
          <div class="form-group">
            <label>
              <input type="checkbox" v-model="paragraphForm.is_active">
              启用此段落
            </label>
          </div>
          
          <div class="form-actions">
            <button type="submit" :disabled="loading">保存</button>
            <button type="button" @click="showParagraphForm = false">取消</button>
          </div>
        </form>
      </div>
    </div>

    <!-- 文档表单 -->
    <div v-if="showDocumentForm" class="form-overlay">
      <div class="document-form">
        <h3>{{ editingDocument ? '编辑文档' : '添加文档' }}</h3>
        <form @submit.prevent="saveDocument">
          <div class="form-group">
            <label>文档标题</label>
            <input 
              type="text" 
              v-model="documentForm.title" 
              :class="{ error: documentErrors.title }"
              required
            >
            <span v-if="documentErrors.title" class="error-text">{{ documentErrors.title }}</span>
          </div>
          
          <div class="form-group">
            <label>文档描述</label>
            <textarea 
              v-model="documentForm.description" 
              :class="{ error: documentErrors.description }"
              required
            ></textarea>
            <span v-if="documentErrors.description" class="error-text">{{ documentErrors.description }}</span>
          </div>
          
          <div class="form-group">
            <label>文档类别</label>
            <select v-model="documentForm.category">
              <option value="solution">解决方案</option>
              <option value="manual">使用手册</option>
              <option value="specification">技术规格</option>
              <option value="other">其他</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>上传文件</label>
            <input 
              type="file" 
              @change="handleDocumentFileUpload"
              :class="{ error: documentErrors.file }"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            >
            <span v-if="documentErrors.file" class="error-text">{{ documentErrors.file }}</span>
            <span v-if="documentForm.fileName" class="file-name">当前文件: {{ documentForm.fileName }}</span>
          </div>
          
          <div class="form-actions">
            <button type="submit" :disabled="loading">保存</button>
            <button type="button" @click="showDocumentForm = false">取消</button>
          </div>
        </form>
      </div>
    </div>

    <!-- 添加/编辑解决方案表单 -->
    <div v-if="showForm" class="solution-form-overlay">
      <div class="solution-form">
        <h2>{{ isEditing ? '编辑解决方案' : '添加新解决方案' }}</h2>
        <form @submit.prevent="submitForm">
          <div class="form-group">
            <label>标题（中文）</label>
            <input type="text" v-model="formData.title" required>
          </div>
          <div class="form-group">
            <label>标题（英文）</label>
            <input type="text" v-model="formData.titleEn" placeholder="Title (English)">
          </div>
          <div class="form-group">
            <label>类别（中文）</label>
            <input 
              type="text" 
              v-model="formData.category" 
              placeholder="请输入解决方案类别，如：智慧通行、智慧停车、智慧安防等"
              required
              list="category-suggestions"
            >
            <datalist id="category-suggestions">
              <option v-for="cat in availableCategories" :key="cat.value" :value="cat.value">
                {{ cat.label }}
              </option>
            </datalist>
            <div class="category-suggestions">
              <span class="suggestion-label">常用类别：</span>
              <button 
                type="button" 
                v-for="cat in availableCategories" 
                :key="cat.value" 
                @click="formData.category = cat.value"
                class="category-suggestion-btn"
              >
                {{ cat.label }}
              </button>
            </div>
          </div>
          <div class="form-group">
            <label>类别（英文）</label>
            <input
              type="text"
              v-model="formData.categoryEn"
              placeholder="Category (English), e.g.: Smart Access, Smart Parking, Smart Security"
            >
          </div>
          <div class="form-group">
            <label>简要描述（中文）</label>
            <textarea v-model="formData.briefDescription" placeholder="简要描述解决方案"></textarea>
          </div>
          <div class="form-group">
            <label>简要描述（英文）</label>
            <textarea v-model="formData.briefDescriptionEn" placeholder="Brief Description (English)"></textarea>
          </div>
          <div class="form-group">
            <label>详细描述（中文）</label>
            <textarea v-model="formData.description" required placeholder="详细描述解决方案"></textarea>
          </div>
          <div class="form-group">
            <label>详细描述（英文）</label>
            <textarea v-model="formData.descriptionEn" placeholder="Description (English)"></textarea>
          </div>


          <div class="form-actions">
            <button type="submit">保存</button>
            <button type="button" @click="showForm = false">取消</button>
          </div>
        </form>
      </div>
    </div>

    <!-- 确认删除对话框 -->
    <div v-if="showDeleteDialog" class="dialog-overlay">
      <div class="dialog">
        <h3>确认删除</h3>
        <p>确定要删除解决方案「{{ currentSolution?.title }}」吗？此操作不可撤销。</p>
        <div class="dialog-actions">
          <button @click="handleDeleteCancel" class="btn-cancel" :disabled="loading">取消</button>
          <button @click="handleDelete" class="btn-delete" :disabled="loading">{{ loading ? '删除中...' : '确认删除' }}</button>
        </div>
      </div>
    </div>
    <div v-if="loading" class="loading-overlay">...</div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { 
  getAllSolutions, 
  addSolution, 
  updateSolution, 
  deleteSolution,
  getSolutionParagraphs,
  createSolutionParagraph,
  updateSolutionParagraph,
  deleteSolutionParagraph
} from '@/api/solution';
// 添加以下导入语句
import SolutionTable from '@/components/admin/SolutionTable.vue';
import SolutionFilter from '@/components/admin/SolutionFilter.vue';
// 导入解决方案模板
import { solutionCategories } from '@/config/solutionTemplate.js';
// 导入XLSX库用于Excel文件处理
import * as XLSX from 'xlsx';

// 状态变量
const isManagementView = ref(true);
const solutions = ref([]);
const loading = ref(false);
const showDeleteDialog = ref(false);
const currentSolution = ref(null);
const showForm = ref(false);
const isEditing = ref(false);
const currentSolutionId = ref('');



// 段落管理相关状态
const solutionParagraphs = ref([]);
const showParagraphForm = ref(false);
const editingParagraph = ref(null);
const selectedSolutionForParagraphs = ref(null);
const paragraphErrors = ref({});
const draggedParagraph = ref(null);

// 表单数据
const formData = ref({
  title: '',
  titleEn: '',
  category: '',
  categoryEn: '',
  description: '',
  descriptionEn: '',
  briefDescription: '',
  briefDescriptionEn: ''
});

// 可用的解决方案类别
const availableCategories = ref(solutionCategories);



// 段落表单数据
const paragraphForm = ref({
  id: null,
  title: '',
  content: '',
  content_type: 'text', // text, table, image, mixed
  order_index: 0,
  is_active: true
});

// 图片上传相关状态
const selectedImageFile = ref(null);
const imagePreviewUrl = ref('');
const useManualUrl = ref(false);

// 表格编辑相关状态
const tableInputMode = ref('visual'); // visual, import, json
const tableData = ref({
  headers: ['列1', '列2'],
  rows: [['', '']]
});
const tableJsonContent = ref('');
const importPreviewData = ref(null);
const selectedTableFile = ref(null);

// 混合内容编辑相关状态
const mixedContentBlocks = ref([]);
const mixedInputMode = ref('visual'); // visual, json
const mixedJsonContent = ref('');
const showAddBlockMenu = ref(false);

// 富文本编辑器相关状态
const richTextEditor = ref(null);
const mixedRichTextEditors = ref({});



// 富文本编辑器相关方法
const formatText = (command, value = null) => {
  if (richTextEditor.value) {
    richTextEditor.value.focus();
    document.execCommand(command, false, value);
    updateParagraphContent();
  }
};

const clearFormatting = () => {
  if (richTextEditor.value) {
    richTextEditor.value.focus();
    document.execCommand('removeFormat', false, null);
    updateParagraphContent();
  }
};

const handleRichTextInput = () => {
  updateParagraphContent();
};

const handleRichTextPaste = () => {
  // 允许粘贴，保留基本格式
  setTimeout(() => {
    updateParagraphContent();
  }, 10);
};

const updateParagraphContent = () => {
  if (richTextEditor.value) {
    paragraphForm.value.content = richTextEditor.value.innerHTML;
  }
};

const setRichTextContent = (content) => {
  if (richTextEditor.value) {
    richTextEditor.value.innerHTML = content || '';
  }
};

// 混合内容富文本编辑器相关方法
const formatMixedText = (command, blockIndex) => {
  const editor = mixedRichTextEditors.value[blockIndex];
  if (editor) {
    editor.focus();
    document.execCommand(command, false, null);
    updateMixedBlockContent(blockIndex);
  }
};

const formatMixedTextWithValue = (command, value, blockIndex) => {
  const editor = mixedRichTextEditors.value[blockIndex];
  if (editor) {
    editor.focus();
    document.execCommand(command, false, value);
    updateMixedBlockContent(blockIndex);
  }
};

const clearMixedFormatting = (blockIndex) => {
  const editor = mixedRichTextEditors.value[blockIndex];
  if (editor) {
    editor.focus();
    document.execCommand('removeFormat', false, null);
    updateMixedBlockContent(blockIndex);
  }
};

const handleMixedRichTextInput = (blockIndex) => {
  updateMixedBlockContent(blockIndex);
};

const handleMixedRichTextPaste = (event, blockIndex) => {
  // 允许粘贴，保留基本格式
  setTimeout(() => {
    updateMixedBlockContent(blockIndex);
  }, 10);
};

const updateMixedBlockContent = (blockIndex) => {
  const editor = mixedRichTextEditors.value[blockIndex];
  if (editor && mixedContentBlocks.value[blockIndex]) {
    mixedContentBlocks.value[blockIndex].content = editor.innerHTML;
  }
};



// 注释掉暂时未使用的函数
// const setMixedRichTextContent = (blockIndex, content) => {
//   const editor = mixedRichTextEditors.value[blockIndex];
//   if (editor) {
//     editor.innerHTML = content || '';
//   }
// };

// 表格编辑相关方法
const handleTableInputModeChange = () => {
  if (tableInputMode.value === 'visual') {
    // 如果切换到可视化编辑，尝试解析现有JSON内容
    if (tableJsonContent.value) {
      try {
        const parsed = JSON.parse(tableJsonContent.value);
        if (parsed.headers && parsed.rows) {
          tableData.value = parsed;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  } else if (tableInputMode.value === 'json') {
    // 如果切换到JSON编辑，将可视化数据转换为JSON
    if (tableData.value.headers.length > 0 || tableData.value.rows.length > 0) {
      tableJsonContent.value = JSON.stringify(tableData.value, null, 2);
    }
  }
};

const addTableRow = () => {
  const newRow = new Array(tableData.value.headers.length).fill('');
  tableData.value.rows.push(newRow);
};

const removeTableRow = (index) => {
  if (tableData.value.rows.length > 1) {
    tableData.value.rows.splice(index, 1);
  }
};

const addTableColumn = () => {
  tableData.value.headers.push('新列');
  tableData.value.rows.forEach(row => {
    row.push('');
  });
};

const removeTableColumn = (index) => {
  if (tableData.value.headers.length > 1) {
    tableData.value.headers.splice(index, 1);
    tableData.value.rows.forEach(row => {
      row.splice(index, 1);
    });
  }
};

const handleTableFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedTableFile.value = file;
    parseTableFile(file);
  }
};

const parseTableFile = (file) => {
  const reader = new FileReader();
  
  if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
    // Excel文件处理
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // 获取第一个工作表
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // 将工作表转换为JSON数组
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (!jsonData || jsonData.length === 0) {
          throw new Error('Excel文件中没有数据');
        }
        
        // 第一行作为表头，其余行作为数据
        const headers = jsonData[0].map(h => String(h || '').trim());
        const rows = jsonData.slice(1).map(row => 
          row.map(cell => String(cell || '').trim())
        ).filter(row => row.some(cell => cell)); // 过滤空行
        
        if (headers.length === 0) {
          throw new Error('Excel文件表头不能为空');
        }
        
        importPreviewData.value = { headers, rows };
      } catch (error) {
        console.error('Excel解析错误:', error);
        alert('Excel文件解析失败：' + error.message);
      }
    };
    reader.readAsArrayBuffer(file);
  } else {
    // CSV和JSON文件处理
    reader.onload = (e) => {
      const content = e.target.result;
      try {
        if (file.name.endsWith('.csv')) {
          parseCSV(content);
        } else if (file.name.endsWith('.json')) {
          parseJSON(content);
        } else {
          alert('仅支持CSV、Excel(.xlsx/.xls)和JSON格式文件');
        }
      } catch (error) {
        alert('文件解析失败：' + error.message);
      }
    };
    reader.readAsText(file);
  }
};

const parseCSV = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV文件至少需要包含表头和一行数据');
  }
  
  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
  const rows = lines.slice(1).map(line => 
    line.split(',').map(cell => cell.trim().replace(/"/g, ''))
  );
  
  importPreviewData.value = { headers, rows };
};

const parseJSON = (content) => {
  const data = JSON.parse(content);
  if (!data.headers || !data.rows) {
    throw new Error('JSON文件必须包含headers和rows字段');
  }
  importPreviewData.value = data;
};

const confirmTableImport = () => {
  if (importPreviewData.value) {
    tableData.value = { ...importPreviewData.value };
    tableInputMode.value = 'visual';
    importPreviewData.value = null;
    selectedTableFile.value = null;
  }
};

const cancelTableImport = () => {
  importPreviewData.value = null;
  selectedTableFile.value = null;
};

const clearTable = () => {
  tableData.value = {
    headers: ['列1', '列2'],
    rows: [['', '']]
  };
};

const handleJsonInput = () => {
  // 实时验证JSON格式
  if (tableJsonContent.value) {
    try {
      JSON.parse(tableJsonContent.value);
      paragraphErrors.value.content = '';
    } catch (e) {
      paragraphErrors.value.content = 'JSON格式不正确';
    }
  }
};

// 处理内容类型变化
const handleContentTypeChange = () => {
  // 清空内容
  paragraphForm.value.content = '';
  
  // 重置图片相关状态
  selectedImageFile.value = null;
  imagePreviewUrl.value = '';
  useManualUrl.value = false;
  
  // 重置表格相关状态
  if (paragraphForm.value.content_type === 'table') {
    tableInputMode.value = 'visual';
    tableData.value = {
      headers: ['列1', '列2'],
      rows: [['', '']]
    };
    tableJsonContent.value = '';
  }
  
  // 重置混合内容相关状态
  if (paragraphForm.value.content_type === 'mixed') {
    mixedInputMode.value = 'visual';
    mixedContentBlocks.value = [];
    mixedJsonContent.value = '';
    showAddBlockMenu.value = false;
  }
  
  // 重置导入状态
  importPreviewData.value = null;
  selectedTableFile.value = null;
};

// 混合内容编辑器相关方法
const handleMixedInputModeChange = () => {
  if (mixedInputMode.value === 'visual') {
    // 切换到可视化编辑，尝试解析JSON内容
    if (mixedJsonContent.value) {
      try {
        const parsed = JSON.parse(mixedJsonContent.value);
        if (Array.isArray(parsed)) {
          mixedContentBlocks.value = parsed;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  } else if (mixedInputMode.value === 'json') {
    // 切换到JSON编辑，将可视化数据转换为JSON
    if (mixedContentBlocks.value.length > 0) {
      mixedJsonContent.value = JSON.stringify(mixedContentBlocks.value, null, 2);
    }
  }
};

const addMixedContentBlock = (type) => {
  const newBlock = {
    type: type,
    content: type === 'text' ? '请输入文本内容' : 
             type === 'image' ? '' : 
             type === 'table' ? { headers: ['列1', '列2'], rows: [['', '']] } : ''
  };
  
  if (type === 'image') {
    newBlock.alt = '图片描述';
    newBlock.useManualUrl = false;
    newBlock.selectedFile = null;
    newBlock.previewUrl = '';
  }
  
  mixedContentBlocks.value.push(newBlock);
  showAddBlockMenu.value = false;
};

const removeMixedContentBlock = (index) => {
  mixedContentBlocks.value.splice(index, 1);
};

const moveMixedContentBlock = (index, direction) => {
  const newIndex = direction === 'up' ? index - 1 : index + 1;
  if (newIndex >= 0 && newIndex < mixedContentBlocks.value.length) {
    const temp = mixedContentBlocks.value[index];
    mixedContentBlocks.value[index] = mixedContentBlocks.value[newIndex];
    mixedContentBlocks.value[newIndex] = temp;
  }
};

const addTableRowToMixedBlock = (blockIndex) => {
  const block = mixedContentBlocks.value[blockIndex];
  if (block && block.type === 'table' && block.content) {
    const newRow = new Array(block.content.headers.length).fill('');
    block.content.rows.push(newRow);
  }
};

const removeTableRowFromMixedBlock = (blockIndex, rowIndex) => {
  const block = mixedContentBlocks.value[blockIndex];
  if (block && block.type === 'table' && block.content && block.content.rows.length > 1) {
    block.content.rows.splice(rowIndex, 1);
  }
};

const addTableColumnToMixedBlock = (blockIndex) => {
  const block = mixedContentBlocks.value[blockIndex];
  if (block && block.type === 'table' && block.content) {
    block.content.headers.push('新列');
    block.content.rows.forEach(row => row.push(''));
  }
};

const removeTableColumnFromMixedBlock = (blockIndex, colIndex) => {
  const block = mixedContentBlocks.value[blockIndex];
  if (block && block.type === 'table' && block.content && block.content.headers.length > 1) {
    block.content.headers.splice(colIndex, 1);
    block.content.rows.forEach(row => row.splice(colIndex, 1));
  }
};

// 混合内容图片块文件上传处理
const handleMixedImageFileSelect = (event, blockIndex) => {
  const file = event.target.files[0];
  const block = mixedContentBlocks.value[blockIndex];
  
  if (file && block && block.type === 'image') {
    // 保存文件引用
    block.selectedFile = file;
    
    // 创建预览URL
    const reader = new FileReader();
    reader.onload = (e) => {
      block.previewUrl = e.target.result;
    };
    reader.readAsDataURL(file);
  }
};

const resetMixedImageBlock = (blockIndex) => {
    const block = mixedContentBlocks.value[blockIndex];
    if (block && block.type === 'image') {
      // 重置图片块状态
      block.selectedFile = null;
      block.previewUrl = '';
      block.content = '';
      
      // 确保useManualUrl属性存在
      if (block.useManualUrl === undefined) {
        block.useManualUrl = false;
      }
    }
  };
  
  // 处理混合内容中的图片文件上传
  const processMixedContentBlocks = async () => {
    const processedBlocks = [];
    
    for (const block of mixedContentBlocks.value) {
      const processedBlock = { ...block };
      
      if (block.type === 'image' && block.selectedFile && !block.useManualUrl) {
        try {
          // 上传图片文件
          const formData = new FormData();
          formData.append('image', block.selectedFile);
          
          const response = await fetch('/api/upload/image', {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            const result = await response.json();
            processedBlock.content = result.url || result.path;
          } else {
            throw new Error('图片上传失败');
          }
        } catch (error) {
          console.error('图片上传失败:', error);
          throw error;
        }
      }
      
      // 清理临时属性
      delete processedBlock.selectedFile;
      delete processedBlock.previewUrl;
      delete processedBlock.useManualUrl;
      
      processedBlocks.push(processedBlock);
    }
    
    return processedBlocks;
  };

// 加载解决方案列表
const loadSolutions = async () => {
  try {
    loading.value = true;
    const response = await getAllSolutions(1, 10000);
    
    if (response.data && response.data.error) {
      throw new Error(response.data.error);
    }
    
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      solutions.value = response.data.data;
    } else {
      solutions.value = [];
      throw new Error('数据格式错误');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || '加载解决方案失败';
    alert(`加载解决方案失败: ${errorMessage}`);
    
    if (errorMessage.includes('Unknown column') || errorMessage.includes('does not exist') || errorMessage.includes('数据库字段未添加')) {
      alert('数据库字段未添加，请先执行 fix-database.sql 脚本添加英文字段');
    }
    
    solutions.value = [];
  } finally {
    loading.value = false;
  }
};

// 处理搜索
const handleSearch = async (searchParams) => {
  try {
    loading.value = true;
    // 传递page=1和一个足够大的pageSize来获取所有数据
    const response = await getAllSolutions(1, 10000);
    
    // 只有当搜索参数存在时才进行过滤
    if (searchParams.keyword || searchParams.category) {
      solutions.value = response.data.data.filter(solution => {
        // 标题模糊搜索
        const matchesTitle = solution.title.toLowerCase().includes(searchParams.keyword.toLowerCase());
        // 类别筛选
        const matchesCategory = !searchParams.category || solution.category === searchParams.category;
        return matchesTitle && matchesCategory;
      });
    } else {
      // 没有搜索参数时显示所有数据
      solutions.value = response.data.data;
    }
  } catch (error) {
    console.error('搜索解决方案失败:', error);
    alert('搜索解决方案失败，请重试');
  } finally {
    loading.value = false;
  }
};

// 编辑解决方案
const handleEdit = (solution) => {
  isEditing.value = true;
  currentSolutionId.value = solution.id;
  formData.value = {
    title: solution.title || '',
    titleEn: solution.titleEn || '',
    category: solution.category || '',
    categoryEn: solution.categoryEn || '',
    briefDescription: solution.briefDescription || '',
    briefDescriptionEn: solution.briefDescriptionEn || '',
    description: solution.description || '',
    descriptionEn: solution.descriptionEn || ''
  };
  
  showForm.value = true;
};

// 显示添加表单
const showAddForm = () => {
  isEditing.value = false;
  currentSolutionId.value = '';
  formData.value = {
    title: '',
    titleEn: '',
    category: '',
    categoryEn: '',
    briefDescription: '',
    briefDescriptionEn: '',
    description: '',
    descriptionEn: ''
  };
  
  showForm.value = true;
};

// 提交表单
const submitForm = async () => {
  try {
    loading.value = true;
    
    // 准备数据
    const data = {
      title: formData.value.title,
      titleEn: formData.value.titleEn || '',
      category: formData.value.category,
      categoryEn: formData.value.categoryEn || '',
      description: formData.value.description,
      descriptionEn: formData.value.descriptionEn || '',
      briefDescription: formData.value.briefDescription,
      briefDescriptionEn: formData.value.briefDescriptionEn || ''
    };

    if (isEditing.value) {
      // 编辑模式下，使用PUT方法更新解决方案
      await updateSolution(currentSolutionId.value, data, { headers: { 'Content-Type': 'application/json' } });
    } else {
      // 新增模式下，使用POST方法添加解决方案
      await addSolution(data, { headers: { 'Content-Type': 'application/json' } });
    }

    // 关闭表单并刷新列表
    showForm.value = false;
    loadSolutions();
  } catch (err) {
    console.error('保存解决方案失败:', err);
    alert('保存失败，请稍后重试: ' + (err.message || ''));
  } finally {
    loading.value = false;
  }
};

// 确认删除解决方案
const handleDeleteConfirm = (solution) => {
  currentSolution.value = solution;
  showDeleteDialog.value = true;
};

// 取消删除
const handleDeleteCancel = () => {
  showDeleteDialog.value = false;
  currentSolution.value = null;
};

// 执行删除
const handleDelete = async () => {
  if (!currentSolution.value) return;

  try {
    loading.value = true;
    await deleteSolution(currentSolution.value.id);
    // 删除成功后重新加载列表
    await loadSolutions();
    showDeleteDialog.value = false;
    alert('解决方案删除成功');
  } catch (error) {
    console.error('删除解决方案失败:', error);
    alert('删除失败，请重试');
  } finally {
    loading.value = false;
    currentSolution.value = null;
  }
};






// 段落管理相关方法
const showParagraphManagement = async (solution) => {
  selectedSolutionForParagraphs.value = solution;
  await loadSolutionParagraphs(solution.id);
};

const loadSolutionParagraphs = async (solutionId) => {
  try {
    loading.value = true;
    // 管理界面需要获取所有段落（包括禁用的），所以activeOnly=false
    const response = await getSolutionParagraphs(solutionId, false);
    solutionParagraphs.value = response.data?.data || [];
  } catch (error) {
    console.error('加载段落失败:', error);
    alert('加载段落失败');
  } finally {
    loading.value = false;
  }
};

const showAddParagraphForm = () => {
  editingParagraph.value = null;
  paragraphForm.value = {
    id: null,
    title: '',
    content: '',
    content_type: 'text',
    order_index: solutionParagraphs.value.length + 1,
    is_active: true
  };
  paragraphErrors.value = {};
  // 重置图片上传状态
  selectedImageFile.value = null;
  imagePreviewUrl.value = '';
  useManualUrl.value = false;
  showParagraphForm.value = true;
};

const editParagraph = (paragraph) => {
  editingParagraph.value = paragraph;
  paragraphForm.value = {
    id: paragraph.id,
    title: paragraph.title,
    content: paragraph.content,
    content_type: paragraph.content_type,
    order_index: paragraph.order_index,
    is_active: paragraph.is_active
  };
  paragraphErrors.value = {};
  
  // 重置图片上传状态
  selectedImageFile.value = null;
  imagePreviewUrl.value = '';
  useManualUrl.value = false;
  
  // 重置表格编辑状态
  tableInputMode.value = 'visual';
  tableData.value = {
    headers: ['列1', '列2'],
    rows: [['', '']]
  };
  tableJsonContent.value = '';
  importPreviewData.value = null;
  selectedTableFile.value = null;
  
  // 重置混合内容编辑状态
  mixedInputMode.value = 'visual';
  mixedContentBlocks.value = [];
  mixedJsonContent.value = '';
  showAddBlockMenu.value = false;
  
  // 如果是文本类型，设置富文本编辑器内容
  if (paragraph.content_type === 'text' && paragraph.content) {
    // 延迟设置内容，确保DOM已渲染
    setTimeout(() => {
      setRichTextContent(paragraph.content);
    }, 100);
  }
  
  // 如果是图片类型且有内容，显示预览
  if (paragraph.content_type === 'image' && paragraph.content) {
    if (paragraph.content.startsWith('http') || paragraph.content.startsWith('/')) {
      // 如果是URL，设置为手动URL模式
      useManualUrl.value = true;
      imagePreviewUrl.value = paragraph.content;
    }
  }
  
  // 如果是表格类型且有内容，解析表格数据
  if (paragraph.content_type === 'table' && paragraph.content) {
    try {
      let parsedData;
      if (typeof paragraph.content === 'string') {
        // 如果是字符串，尝试解析JSON
        parsedData = JSON.parse(paragraph.content);
        tableJsonContent.value = paragraph.content;
      } else if (typeof paragraph.content === 'object') {
        // 如果已经是对象，直接使用
        parsedData = paragraph.content;
        tableJsonContent.value = JSON.stringify(paragraph.content, null, 2);
      }
      
      if (parsedData && parsedData.headers && parsedData.rows) {
        tableData.value = parsedData;
      }
    } catch (e) {
      if (typeof paragraph.content === 'string') {
        tableJsonContent.value = paragraph.content;
      } else {
        tableJsonContent.value = JSON.stringify(paragraph.content || {}, null, 2);
      }
    }
  }
  
  // 如果是混合内容类型且有内容，解析混合内容数据
  if (paragraph.content_type === 'mixed' && paragraph.content) {
    try {
      let parsedData;
      if (typeof paragraph.content === 'string') {
        // 如果是字符串，尝试解析JSON
        parsedData = JSON.parse(paragraph.content);
        mixedJsonContent.value = paragraph.content;
      } else if (Array.isArray(paragraph.content)) {
        // 如果已经是数组，直接使用
        parsedData = paragraph.content;
        mixedJsonContent.value = JSON.stringify(paragraph.content, null, 2);
      } else if (typeof paragraph.content === 'object') {
        // 如果是对象但不是数组，包装成数组
        parsedData = [paragraph.content];
        mixedJsonContent.value = JSON.stringify([paragraph.content], null, 2);
      }
      
      if (Array.isArray(parsedData)) {
        // 为每个图片块初始化必要的属性
        mixedContentBlocks.value = parsedData.map(block => {
          if (block.type === 'image') {
            return {
              ...block,
              useManualUrl: true, // 编辑时默认为URL模式
              selectedFile: null,
              previewUrl: ''
            };
          }
          return block;
        });
      }
    } catch (e) {
      if (typeof paragraph.content === 'string') {
        mixedJsonContent.value = paragraph.content;
      } else {
        mixedJsonContent.value = JSON.stringify(paragraph.content || [], null, 2);
      }
    }
  }
  
  showParagraphForm.value = true;
};

const saveParagraph = async () => {
  if (!validateParagraphForm()) {
    return;
  }

  try {
    loading.value = true;
    const solutionId = selectedSolutionForParagraphs.value.id;
    
    // 检查是否需要上传图片文件
    const needsFileUpload = paragraphForm.value.content_type === 'image' && selectedImageFile.value && !useManualUrl.value;
    
    // 检查混合内容中是否有需要上传的图片文件
    const hasMixedImageUploads = paragraphForm.value.content_type === 'mixed' && 
      mixedInputMode.value === 'visual' && 
      mixedContentBlocks.value.some(block => 
        block.type === 'image' && block.selectedFile && !block.useManualUrl
      );
    
    let paragraphData;
    let requestConfig = {};
    
    if (needsFileUpload || hasMixedImageUploads) {
      // 使用FormData进行文件上传
      paragraphData = new FormData();
      paragraphData.append('title', paragraphForm.value.title);
      paragraphData.append('content_type', paragraphForm.value.content_type);
      paragraphData.append('order_index', paragraphForm.value.order_index);
      paragraphData.append('is_active', paragraphForm.value.is_active);
      
      if (needsFileUpload) {
        // 单独图片段落的文件上传
        paragraphData.append('image', selectedImageFile.value);
      } else if (hasMixedImageUploads) {
        // 混合内容中的图片文件上传
        const processedBlocks = await processMixedContentBlocks();
        paragraphData.append('content', JSON.stringify(processedBlocks));
      }
      
      requestConfig = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
    } else {
      // 处理表格内容和混合内容
      let content = paragraphForm.value.content;
      if (paragraphForm.value.content_type === 'table') {
        if (tableInputMode.value === 'visual') {
          // 将可视化表格数据转换为JSON字符串
          content = JSON.stringify(tableData.value);
        } else if (tableInputMode.value === 'json') {
          // 使用JSON输入的内容
          content = tableJsonContent.value;
        }
      } else if (paragraphForm.value.content_type === 'mixed') {
        if (mixedInputMode.value === 'visual') {
          // 将可视化混合内容数据转换为JSON字符串
          content = JSON.stringify(mixedContentBlocks.value);
        } else if (mixedInputMode.value === 'json') {
          // 使用JSON输入的内容
          content = mixedJsonContent.value;
        }
      }
      
      // 使用JSON格式
      paragraphData = {
        title: paragraphForm.value.title,
        content: content,
        content_type: paragraphForm.value.content_type,
        order_index: paragraphForm.value.order_index,
        is_active: paragraphForm.value.is_active
      };
    }

    if (editingParagraph.value) {
      await updateSolutionParagraph(solutionId, paragraphForm.value.id, paragraphData, requestConfig);
      alert('段落更新成功');
    } else {
      await createSolutionParagraph(solutionId, paragraphData, requestConfig);
      alert('段落创建成功');
    }

    showParagraphForm.value = false;
    await loadSolutionParagraphs(solutionId);
  } catch (error) {
    console.error('保存段落失败:', error);
    alert('保存段落失败');
  } finally {
    loading.value = false;
  }
};

const validateParagraphForm = () => {
  paragraphErrors.value = {};
  let isValid = true;

  if (!paragraphForm.value.title.trim()) {
    paragraphErrors.value.title = '请输入段落标题';
    isValid = false;
  }

  // 对于图片类型，检查是否有文件上传或手动输入的URL
  if (paragraphForm.value.content_type === 'image') {
    if (!selectedImageFile.value && !paragraphForm.value.content.trim()) {
      paragraphErrors.value.content = '请上传图片文件或输入图片URL';
      isValid = false;
    }
  } else if (paragraphForm.value.content_type === 'table') {
    // 表格类型的验证在后面的JSON验证部分处理
  } else if (paragraphForm.value.content_type !== 'mixed') {
    // 对于其他非图片、非混合内容类型，检查content字段
    if (!paragraphForm.value.content.trim()) {
      paragraphErrors.value.content = '请输入段落内容';
      isValid = false;
    }
  }

  // 验证JSON格式
  if (paragraphForm.value.content_type === 'table') {
    if (tableInputMode.value === 'json') {
      // JSON模式下验证JSON格式
      if (!tableJsonContent.value.trim()) {
        paragraphErrors.value.content = '请输入表格JSON数据';
        isValid = false;
      } else {
        try {
          JSON.parse(tableJsonContent.value);
        } catch (e) {
          paragraphErrors.value.content = '请输入有效的JSON格式';
          isValid = false;
        }
      }
    } else if (tableInputMode.value === 'visual') {
      // 可视化模式下验证表格数据
      if (!tableData.value.headers.length || !tableData.value.rows.length) {
        paragraphErrors.value.content = '请添加表格数据';
        isValid = false;
      }
    }
  } else if (paragraphForm.value.content_type === 'mixed') {
    if (mixedInputMode.value === 'json') {
      // JSON模式下验证JSON格式
      if (!mixedJsonContent.value.trim()) {
        paragraphErrors.value.content = '请输入混合内容JSON数据';
        isValid = false;
      } else {
        try {
          const parsed = JSON.parse(mixedJsonContent.value);
          if (!Array.isArray(parsed)) {
            paragraphErrors.value.content = '混合内容必须是数组格式';
            isValid = false;
          }
        } catch (e) {
          paragraphErrors.value.content = '请输入有效的JSON格式';
          isValid = false;
        }
      }
    } else if (mixedInputMode.value === 'visual') {
      // 可视化模式下验证混合内容数据
      if (mixedContentBlocks.value.length === 0) {
        paragraphErrors.value.content = '请添加至少一个内容块';
        isValid = false;
      } else {
        // 验证每个内容块的完整性
        for (let i = 0; i < mixedContentBlocks.value.length; i++) {
          const block = mixedContentBlocks.value[i];
          if (block.type === 'text' && !block.content.trim()) {
            paragraphErrors.value.content = `第${i + 1}个文本块内容不能为空`;
            isValid = false;
            break;
          } else if (block.type === 'image') {
            // 对于图片块，检查是否有URL或上传的文件
            const hasUrl = block.content && block.content.trim();
            const hasFile = block.selectedFile;
            if (!hasUrl && !hasFile) {
              paragraphErrors.value.content = `第${i + 1}个图片块需要提供URL或上传文件`;
              isValid = false;
              break;
            }
          } else if (block.type === 'table' && (!block.content || !block.content.headers || !block.content.rows)) {
            paragraphErrors.value.content = `第${i + 1}个表格块数据不完整`;
            isValid = false;
            break;
          }
        }
      }
    }
  }

  return isValid;
};



// 图片上传处理方法
const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  
  // 验证文件大小（10MB限制）
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    alert('文件大小不能超过10MB');
    event.target.value = '';
    return;
  }
  
  // 验证文件类型
  const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    alert('请选择PNG、JPG、JPEG、GIF或WEBP格式的图片');
    event.target.value = '';
    return;
  }
  
  selectedImageFile.value = file;
  
  // 创建预览URL
  const reader = new FileReader();
  reader.onload = (e) => {
    imagePreviewUrl.value = e.target.result;
  };
  reader.readAsDataURL(file);
  
  // 清除手动URL模式
  useManualUrl.value = false;
  paragraphForm.value.content = '';
};

// 移除图片
const removeImage = () => {
  selectedImageFile.value = null;
  imagePreviewUrl.value = '';
  paragraphForm.value.content = '';
  if (document.getElementById('image-upload')) {
    document.getElementById('image-upload').value = '';
  }
};

// 切换URL输入模式
const handleUrlModeChange = () => {
  if (useManualUrl.value) {
    // 切换到手动URL模式，清除文件上传
    selectedImageFile.value = null;
    imagePreviewUrl.value = '';
    if (document.getElementById('image-upload')) {
      document.getElementById('image-upload').value = '';
    }
  } else {
    // 切换到文件上传模式，清除URL
    paragraphForm.value.content = '';
  }
};

const toggleParagraphStatus = async (paragraph) => {
  try {
    const solutionId = selectedSolutionForParagraphs.value.id;
    const updatedParagraph = {
      title: paragraph.title,
      content: paragraph.content,
      content_type: paragraph.content_type,
      order_index: paragraph.order_index,
      is_active: !paragraph.is_active
    };
    await updateSolutionParagraph(solutionId, paragraph.id, updatedParagraph);
    alert(`段落已${updatedParagraph.is_active ? '启用' : '禁用'}`);
    await loadSolutionParagraphs(solutionId);
  } catch (error) {
    console.error('更新段落状态失败:', error);
    alert('更新段落状态失败');
  }
};

const handleDeleteParagraph = async (paragraphId) => {
  if (!confirm('确定要删除这个段落吗？')) {
    return;
  }

  try {
    const solutionId = selectedSolutionForParagraphs.value.id;
    await deleteSolutionParagraph(solutionId, paragraphId);
    alert('段落删除成功');
    await loadSolutionParagraphs(solutionId);
  } catch (error) {
    console.error('删除段落失败:', error);
    alert('删除段落失败');
  }
};

// 拖拽排序
const handleDragStart = (paragraph, index) => {
  draggedParagraph.value = { paragraph, index };
};

const handleDrop = async (targetIndex) => {
  if (!draggedParagraph.value || draggedParagraph.value.index === targetIndex) {
    return;
  }

  try {
    const reorderedParagraphs = [...solutionParagraphs.value];
    const [draggedItem] = reorderedParagraphs.splice(draggedParagraph.value.index, 1);
    reorderedParagraphs.splice(targetIndex, 0, draggedItem);

    // 更新order_index
    const updatePromises = reorderedParagraphs.map((paragraph, index) => {
      return updateSolutionParagraph(paragraph.id, {
        ...paragraph,
        order_index: index + 1
      });
    });

    await Promise.all(updatePromises);
    alert('段落排序更新成功');
    await loadSolutionParagraphs(selectedSolutionForParagraphs.value.id);
  } catch (error) {
    console.error('更新段落排序失败:', error);
    alert('更新段落排序失败');
  } finally {
    draggedParagraph.value = null;
  }
};

const getContentTypeLabel = (type) => {
  const labels = {
    text: '文本内容',
    table: '表格数据',
    image: '图片',
    mixed: '混合内容'
  };
  return labels[type] || type;
};

const getContentPreview = (content) => {
  if (!content) return '';
  if (content.length > 100) {
    return content.substring(0, 100) + '...';
  }
  return content;
};

onMounted(() => {
  loadSolutions();
});
</script>

<style scoped lang="scss">
.admin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

// 添加解决方案表单样式
.solution-form {
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  max-height: 70vh; /* 固定最大高度为视口高度的70% */
  overflow-y: auto; /* 内容过长时显示垂直滚动条 */
}

.solution-form h2 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #555;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.form-group textarea {
  min-height: 80px;
  resize: vertical;
}

.preview-image {
  max-width: 200px;
  max-height: 150px;
  margin-top: 10px;
  border-radius: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.btn-add {
  background-color: #42b983;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-add:hover {
  background-color: #359469;
}

.table-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

/* 对话框样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.dialog {
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
}

.dialog h3 {
  margin-top: 0;
  color: #333;
}

.dialog p {
  color: #666;
  margin-bottom: 24px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-cancel {
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-delete {
  background-color: #ff4444;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-delete:disabled,
.btn-cancel:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

/* 加载中样式 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255,255,255,0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

/* 视图切换样式 */
.view-toggle {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.view-toggle button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
}

.view-toggle button.active {
  background-color: #42b983;
  color: white;
  border-color: #42b983;
}

/* 解决方案表单样式 */
.solution-form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.solution-form {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 800px;
  max-width: 90%;
}

.form-section {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background-color: #f8f9fa;
}

.form-section h3 {
  margin: 0 0 20px 0;
  color: #495057;
  font-size: 18px;
  font-weight: 600;
  border-bottom: 2px solid #007bff;
  padding-bottom: 8px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #333;
  font-weight: 500;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-group textarea {
  min-height: 100px;
}

.preview-image {
  max-width: 100%;
  max-height: 200px;
  margin-top: 10px;
  border-radius: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.form-actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.form-actions button:first-child {
  background-color: #1890ff;
  color: white;
}

@media (max-width: 768px) {
  .header-actions {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}



.btn-add, .btn-close {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.btn-add {
  background: #28a745;
  color: white;
}

.btn-add:hover {
  background: #218838;
}

.btn-close {
  background: #6c757d;
}

.btn-close:hover {
  background: #5a6268;
}

/* 段落管理样式 */
.paragraph-management {
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.paragraph-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #007bff;
}

.paragraph-header h2 {
  margin: 0;
  color: #007bff;
  font-size: 1.5rem;
}

.paragraph-actions {
  display: flex;
  gap: 10px;
}

.paragraph-list {
  margin-top: 20px;
}

.no-paragraphs {
  text-align: center;
  padding: 40px;
  color: #6c757d;
  font-style: italic;
}

.paragraph-items {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.paragraph-item {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
  transition: all 0.3s ease;
  cursor: move;
}

.paragraph-item:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-color: #007bff;
}

.paragraph-drag-handle {
  color: #6c757d;
  margin-right: 15px;
  font-size: 18px;
  cursor: grab;
}

.paragraph-drag-handle:active {
  cursor: grabbing;
}

.paragraph-info {
  flex: 1;
  margin-right: 15px;
}

.paragraph-info h4 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 16px;
}

.paragraph-type {
  margin: 4px 0;
  color: #007bff;
  font-size: 12px;
  font-weight: 500;
}

.paragraph-preview {
  margin: 8px 0;
  color: #6c757d;
  font-size: 14px;
  line-height: 1.4;
}

.paragraph-meta {
  font-size: 12px;
  color: #868e96;
}

.paragraph-actions {
  display: flex;
  gap: 8px;
}

.paragraph-actions button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.3s;
}

.btn-edit {
  background: #17a2b8;
  color: white;
}

.btn-edit:hover {
  background: #138496;
}

.btn-enable {
  background: #28a745;
  color: white;
}

.btn-enable:hover {
  background: #218838;
}

.btn-disable {
  background: #ffc107;
  color: #212529;
}

.btn-disable:hover {
  background: #e0a800;
}

.btn-delete {
  background: #dc3545;
  color: white;
}

.btn-delete:hover {
  background: #c82333;
}

/* 段落表单样式 */
.paragraph-form {
  background: white;
  padding: 30px;
  border-radius: 8px;
  width: 800px;
  max-width: 95%;
  max-height: 85vh;
  overflow-y: auto;
}

.paragraph-form h3 {
  margin: 0 0 25px 0;
  color: #333;
  font-size: 1.5rem;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
}

.paragraph-form .form-group {
  margin-bottom: 20px;
}

.paragraph-form .form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 600;
}

.paragraph-form .form-group input,
.paragraph-form .form-group textarea,
.paragraph-form .form-group select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.15s ease-in-out;
}

.paragraph-form .form-group input:focus,
.paragraph-form .form-group textarea:focus,
.paragraph-form .form-group select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.paragraph-form .form-group input.error,
.paragraph-form .form-group textarea.error {
  border-color: #dc3545;
}

.paragraph-form .error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}

.paragraph-form .form-group textarea {
  resize: vertical;
  font-family: 'Courier New', monospace;
}

.paragraph-form .image-preview {
  margin-top: 10px;
  text-align: center;
}

.paragraph-form .image-preview img {
  border: 1px solid #ddd;
  border-radius: 4px;
}

.paragraph-form .form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.paragraph-form .form-actions button {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.paragraph-form .form-actions button[type="submit"] {
  background: #007bff;
  color: white;
}

.paragraph-form .form-actions button[type="submit"]:hover {
  background: #0056b3;
}

.paragraph-form .form-actions button[type="submit"]:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.paragraph-form .form-actions button[type="button"] {
  background: #6c757d;
  color: white;
}

.paragraph-form .form-actions button[type="button"]:hover {
  background: #5a6268;
  color: white;
}

.btn-close:hover {
  background: #5a6268;
}



.btn-edit {
  background: #ffc107;
  color: #212529;
}

.btn-edit:hover {
  background: #e0a800;
}

.btn-delete {
  background: #dc3545;
  color: white;
}

.btn-delete:hover {
  background: #c82333;
}

/* 文档表单样式 */
.form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.document-form {
  background: white;
  padding: 30px;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
}

.document-form h3 {
  margin: 0 0 20px 0;
  color: #333;
  text-align: center;
}

.document-form .form-group {
  margin-bottom: 15px;
}

.document-form .form-group label {
  display: block;
  margin-bottom: 5px;
  color: #333;
  font-weight: bold;
}

.document-form .form-group input,
.document-form .form-group textarea,
.document-form .form-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.document-form .form-group input.error,
.document-form .form-group textarea.error {
  border-color: #dc3545;
}

.error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}

.file-name {
  display: block;
  margin-top: 5px;
  font-size: 12px;
  color: #28a745;
  font-style: italic;
}

.document-form .form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.document-form .form-actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.document-form .form-actions button[type="submit"] {
  background: #007bff;
  color: white;
}

.document-form .form-actions button[type="submit"]:hover {
  background: #0056b3;
}

.document-form .form-actions button[type="button"] {
  background: #6c757d;
  color: white;
}

.document-form .form-actions button[type="button"]:hover {
  background: #5a6268;
}

.docs-btn {
  background: #17a2b8;
  color: white;
}

.docs-btn:hover {
  background: #138496;
}

/* 标签页样式 */
.form-tabs {
  margin-bottom: 20px;
}

.tab-navigation {
  display: flex;
  border-bottom: 2px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
  overflow-x: auto;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  border-bottom: 3px solid transparent;
}

.tab-button:hover {
  background: #e9ecef;
  color: #495057;
}

.tab-button.active {
  background: #fff;
  color: #007bff;
  border-bottom-color: #007bff;
  font-weight: 600;
}

.tab-icon {
  font-size: 16px;
}

.tab-label {
  font-size: 14px;
}

.tab-content-container {
  background: #fff;
  border: 1px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 20px;
}

.section-description {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 20px;
  padding: 10px;
  background: #f8f9fa;
  border-left: 4px solid #007bff;
  border-radius: 4px;
}

/* 表格编辑器样式 */
.table-input-options {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
}

.radio-option input[type="radio"] {
  margin: 0;
}

.visual-table-editor {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 15px;
}

.table-controls {
  display: flex;
  gap: 10px;
  padding: 10px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.table-controls button {
  padding: 6px 12px;
  border: 1px solid #007bff;
  background: white;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
}

.table-controls button:hover {
  background: #007bff;
  color: white;
}

.editable-table {
  width: 100%;
  border-collapse: collapse;
}

.editable-table th,
.editable-table td {
  border: 1px solid #dee2e6;
  padding: 8px;
  position: relative;
}

.editable-table th {
  background: #f8f9fa;
  font-weight: 600;
}

.editable-table input {
  width: 100%;
  border: none;
  background: transparent;
  padding: 4px;
  font-size: 14px;
}

.editable-table input:focus {
  outline: 2px solid #007bff;
  outline-offset: -2px;
}

.column-actions,
.row-actions {
  display: flex;
  gap: 5px;
  align-items: center;
}

.column-actions button,
.row-actions button {
  padding: 2px 6px;
  border: none;
  background: #dc3545;
  color: white;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
}

.column-actions button:hover,
.row-actions button:hover {
  background: #c82333;
}

.file-import-section {
  padding: 20px;
  border: 2px dashed #dee2e6;
  border-radius: 6px;
  text-align: center;
  margin-bottom: 15px;
}

.file-import-section.has-file {
  border-color: #28a745;
  background: #f8fff9;
}

.import-preview {
  margin-top: 20px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
}

.import-preview-header {
  background: #f8f9fa;
  padding: 10px 15px;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
}

.import-preview-table {
  width: 100%;
  border-collapse: collapse;
  max-height: 300px;
  overflow-y: auto;
  display: block;
}

.import-preview-table thead,
.import-preview-table tbody {
  display: table;
  width: 100%;
  table-layout: fixed;
}

.import-preview-table th,
.import-preview-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #dee2e6;
  text-align: left;
  word-break: break-word;
}

.import-preview-table th {
  background: #f8f9fa;
  font-weight: 600;
}

.import-actions {
  display: flex;
  gap: 10px;
  padding: 15px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

.import-actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-confirm {
  background: #28a745;
  color: white;
}

.btn-confirm:hover {
  background: #218838;
}

.btn-cancel {
  background: #6c757d;
  color: white;
}

.btn-cancel:hover {
  background: #5a6268;
}

.json-editor {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.4;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px;
  background: #f8f9fa;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .tab-navigation {
    flex-wrap: wrap;
  }
  
  .tab-button {
    flex: 1;
    min-width: 120px;
    justify-content: center;
    padding: 10px 12px;
  }
  
  .tab-icon {
    font-size: 14px;
  }
  
  .tab-label {
    font-size: 12px;
  }
  
  .table-input-options {
    flex-direction: column;
    gap: 10px;
  }
  
  .paragraph-form {
    width: 95%;
  }
}

/* 富文本编辑器样式 */
.rich-text-editor {
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  background: white;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  background: #f8f9fa;
  border-bottom: 1px solid #ddd;
}

.format-btn {
  padding: 6px 10px;
  border: 1px solid #ddd;
  background: white;
  color: #333;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.2s;
  min-width: 32px;
  text-align: center;
}

.format-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.format-btn:active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.rich-text-content {
  min-height: 200px;
  padding: 12px;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  background: white;
}

.rich-text-content:focus {
  box-shadow: inset 0 0 0 2px #007bff;
}

.rich-text-content.error {
  border-color: #dc3545;
}

.rich-text-content[contenteditable]:empty:before {
  content: attr(placeholder);
  color: #6c757d;
  font-style: italic;
}

/* 富文本内容样式 */
.rich-text-content p {
  margin: 0 0 8px 0;
}

.rich-text-content strong, .rich-text-content b {
  font-weight: bold;
}

.rich-text-content em, .rich-text-content i {
  font-style: italic;
}

.rich-text-content u {
  text-decoration: underline;
}

.rich-text-content[style*="text-align: center"] {
  text-align: center;
}

.rich-text-content[style*="text-align: right"] {
  text-align: right;
}

/* 类别建议样式 */
.category-suggestions {
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.suggestion-label {
  font-size: 12px;
  color: #6c757d;
  margin-right: 8px;
  font-weight: 500;
}

.category-suggestion-btn {
  display: inline-block;
  margin: 2px 4px 2px 0;
  padding: 4px 8px;
  background: #fff;
  border: 1px solid #007bff;
  border-radius: 12px;
  color: #007bff;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-suggestion-btn:hover {
  background: #007bff;
  color: #fff;
}

.category-suggestion-btn:active {
  transform: scale(0.98);
}

@media (max-width: 768px) {
  .editor-toolbar {
    gap: 2px;
    padding: 6px;
  }
  
  .format-btn {
     padding: 4px 6px;
     font-size: 11px;
     min-width: 28px;
   }
   
   .rich-text-content {
     padding: 8px;
   }
  
  .table-controls {
    flex-wrap: wrap;
  }
  
  .category-suggestions {
    padding: 6px;
  }
  
  .category-suggestion-btn {
    font-size: 11px;
    padding: 3px 6px;
  }
}
</style>